/**
 * 响应式上下文提供者
 * Feature: mobile-responsive-framework
 * 提供全局响应式配置和断点检测
 */
import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { Grid } from 'antd';

const { useBreakpoint } = Grid;

// 断点类型
export type BreakpointType = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

// 设备类型
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

// 响应式配置接口
export interface ResponsiveConfig {
  // 断点状态
  breakpoint: BreakpointType;
  screens: Partial<Record<BreakpointType, boolean>>;
  
  // 设备类型
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  
  // 触摸设备检测
  isTouchDevice: boolean;
  
  // 屏幕尺寸
  windowWidth: number;
  windowHeight: number;
  
  // 布局配置
  gridGutter: number;
  containerPadding: number;
  
  // 组件尺寸配置
  componentSize: 'small' | 'middle' | 'large';
  formLayout: 'horizontal' | 'vertical';
  tableSize: 'small' | 'middle' | 'large';
  cardSize: 'small' | 'default';
  
  // 模态框/抽屉配置
  modalWidth: number | string;
  drawerWidth: number | string;
  drawerPlacement: 'left' | 'right' | 'top' | 'bottom';
}

// 默认配置
const defaultConfig: ResponsiveConfig = {
  breakpoint: 'lg',
  screens: {},
  deviceType: 'desktop',
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  isTouchDevice: false,
  windowWidth: 1200,
  windowHeight: 800,
  gridGutter: 16,
  containerPadding: 24,
  componentSize: 'middle',
  formLayout: 'horizontal',
  tableSize: 'middle',
  cardSize: 'default',
  modalWidth: 800,
  drawerWidth: 500,
  drawerPlacement: 'right',
};

// 创建上下文
const ResponsiveContext = createContext<ResponsiveConfig>(defaultConfig);

// Provider Props
interface ResponsiveProviderProps {
  children: ReactNode;
}

/**
 * 响应式上下文提供者组件
 */
export const ResponsiveProvider: React.FC<ResponsiveProviderProps> = ({ children }) => {
  const screens = useBreakpoint();
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // 检测触摸设备
    const checkTouchDevice = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (navigator as any).msMaxTouchPoints > 0
      );
    };

    handleResize();
    checkTouchDevice();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 计算响应式配置
  const config = useMemo<ResponsiveConfig>(() => {
    // 确定当前断点
    const getBreakpoint = (): BreakpointType => {
      if (screens.xxl) return 'xxl';
      if (screens.xl) return 'xl';
      if (screens.lg) return 'lg';
      if (screens.md) return 'md';
      if (screens.sm) return 'sm';
      return 'xs';
    };

    const breakpoint = getBreakpoint();
    
    // 确定设备类型
    const isMobile = breakpoint === 'xs';
    const isTablet = breakpoint === 'sm' || breakpoint === 'md';
    const isDesktop = breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === 'xxl';
    
    const deviceType: DeviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

    // 网格间距
    const gridGutter = isMobile ? 8 : isTablet ? 12 : 16;
    
    // 容器内边距
    const containerPadding = isMobile ? 8 : isTablet ? 16 : 24;

    // 组件尺寸
    const componentSize = isMobile ? 'large' : 'middle'; // 移动端用大尺寸便于触摸

    // 表单布局
    const formLayout = isMobile ? 'vertical' : 'horizontal';

    // 表格尺寸
    const tableSize = isMobile ? 'small' : 'middle';

    // 卡片尺寸
    const cardSize = isMobile ? 'small' : 'default';

    // 模态框宽度
    const modalWidth = isMobile ? '95%' : isTablet ? 600 : 800;

    // 抽屉宽度和位置
    const drawerWidth = isMobile ? '100%' : isTablet ? 400 : 500;
    const drawerPlacement = isMobile ? 'bottom' : 'right';

    return {
      breakpoint,
      screens,
      deviceType,
      isMobile,
      isTablet,
      isDesktop,
      isTouchDevice,
      windowWidth: windowSize.width,
      windowHeight: windowSize.height,
      gridGutter,
      containerPadding,
      componentSize,
      formLayout,
      tableSize,
      cardSize,
      modalWidth,
      drawerWidth,
      drawerPlacement,
    };
  }, [screens, windowSize, isTouchDevice]);

  return (
    <ResponsiveContext.Provider value={config}>
      {children}
    </ResponsiveContext.Provider>
  );
};

/**
 * 使用响应式配置的Hook
 */
export const useResponsiveContext = (): ResponsiveConfig => {
  const context = useContext(ResponsiveContext);
  if (!context) {
    console.warn('useResponsiveContext must be used within a ResponsiveProvider');
    return defaultConfig;
  }
  return context;
};

// 导出默认
export default ResponsiveProvider;
